import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, Mail, Globe, MapPin, ArrowRight, Zap } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { SOLAR_STATS } from "@/lib/siteContent";
import { useContent } from "@/lib/content";
import PageHero from "@/components/site/PageHero";
import { Testimonials } from "@/components/Testimonials";
import { Galeria } from "@/components/Galeria";

const EASE = [0.16, 1, 0.3, 1];
const show = (v) => v ?? "Em atualização";

export default function SobrePage() {
  useSeo(
    "Sobre a HB Solar | Energia Solar e Mobilidade Elétrica na Bahia",
    "HB Solar: empresa especializada em soluções energéticas, geração solar fotovoltaica e infraestrutura de recarga para veículos elétricos com a divisão HB Charger."
  );
  const { company: COMPANY } = useContent();

  return (
    <>
      <PageHero
        testId="about-page-title"
        eyebrow="Sobre nós"
        title="Engenharia energética da Bahia para o Brasil"
        desc="A HB Solar é especializada em soluções energéticas: geração solar fotovoltaica e, por meio da divisão HB Charger, infraestrutura completa de recarga para veículos elétricos."
      />

      <section className="py-20 bg-[#FAFAF9]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-start">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: EASE }}
          >
            <h2 className="font-display text-3xl font-bold tracking-tight text-[#0B192C]">
              Da energia do sol à mobilidade elétrica
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-600">
              Nascida na geração distribuída de energia solar, a HB Solar construiu sua
              reputação entregando projetos fotovoltaicos com engenharia própria,
              homologação completa e suporte pós-venda reconhecido pelos clientes.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Com a divisão <strong className="text-[#0B192C]">HB Charger</strong>, a empresa
              amplia sua atuação para a infraestrutura de recarga de veículos elétricos:
              fornecimento de carregadores, projeto elétrico, instalação, comissionamento,
              plataforma de gerenciamento e suporte técnico contínuo.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4">
              {[
                [SOLAR_STATS.projetos, "Projetos solares instalados"],
                [SOLAR_STATS.economia, "De economia na conta de luz"],
                [SOLAR_STATS.garantia, "De garantia dos painéis"],
              ].map(([v, l]) => (
                <div key={l} className="rounded-2xl border border-slate-200 bg-white p-5 text-center">
                  <p className="font-display text-2xl font-extrabold text-[#0B192C]">{v}</p>
                  <p className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{l}</p>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            data-testid="company-data-card"
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
            className="rounded-3xl bg-[#0B192C] p-8 sm:p-10"
          >
            <img src="/logo-hb-solar.png" alt="HB Solar — Soluções Sustentáveis" className="h-14 w-auto rounded-xl bg-white px-2 py-1" />
            <h3 className="mt-6 font-display text-xl font-bold text-white">Dados institucionais</h3>
            <ul className="mt-6 space-y-4 text-sm">
              <li className="flex items-center gap-3 text-slate-300">
                <Phone className="h-4 w-4 shrink-0 text-amber-400" /> {COMPANY.telefone} (WhatsApp)
              </li>
              <li className="flex items-center gap-3 text-slate-300">
                <Mail className="h-4 w-4 shrink-0 text-amber-400" /> {COMPANY.email}
              </li>
              <li className="flex items-center gap-3 text-slate-300">
                <Globe className="h-4 w-4 shrink-0 text-amber-400" /> {COMPANY.dominio}
              </li>
              <li className="flex items-start gap-3 text-slate-300">
                <MapPin className="h-4 w-4 shrink-0 mt-0.5 text-amber-400" />
                <span>
                  Endereço: {show(COMPANY.endereco)}
                  <br />
                  <span className="text-slate-500">Regiões atendidas: {COMPANY.regioes}</span>
                </span>
              </li>
              <li className="text-xs text-slate-500">CNPJ: {show(COMPANY.cnpj)}</li>
            </ul>
            <Link
              to="/contato"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-amber-400 px-6 py-3 text-sm font-extrabold text-[#0B192C] hover:bg-amber-300 transition-colors duration-300"
            >
              <Zap className="h-4 w-4" />
              Falar com a HB Solar
              <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      <Testimonials />
      <Galeria />
    </>
  );
}
